import * as Sentry from "@sentry/react";
import { enmascararEvento, enmascararEventoDelReplay } from "./sentryScrub";

const dsn = process.env.REACT_APP_SENTRY_DSN || "";

const envNumber = (name, fallback) => {
  const rawValue = process.env[name];
  if (!rawValue) return fallback;
  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

export const sentryEnabled = Boolean(dsn);

// Las opciones del SDK, separadas de `init` para que el candado las arranque con la
// configuración REAL y un transporte falso (`sentryDatosPersonales.wave2.test.js`).
export const construirOpcionesDeSentry = () => {
  const tracesSampleRate = envNumber("REACT_APP_SENTRY_TRACES_SAMPLE_RATE", 0);

  return {
    dsn,
    enabled: sentryEnabled,
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.NODE_ENV ||
      "development",
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    tracesSampleRate,
    // Sin esta cabecera en NINGUNA petición: de momento solo se mide lo que tarda cada llamada
    // vista desde el navegador (carga de página, navegación y un span por `fetch`). Unir la
    // traza con la del backend (`sentry-trace`/`baggage`) añade cabeceras a las peticiones
    // cross-origin y es otro cambio, con su propia validación en preview.
    tracePropagationTargets: [],
    // Trazas, errores y breadcrumbs llevan la URL de cada página y de cada `fetch`, y ahí viajan
    // DNIs (`?q=` de la búsqueda de socios) y tokens de QR (`/pre-register/<token>`, `/mesa/<token>`).
    // Los errores ya los mandaban: 4 issues de producción traían el token de preinscripción de un
    // club en su URL (07-10-2026). Los breadcrumbs viajan DENTRO del evento de error (`beforeSend`
    // los recorre) y, en el Replay, como eventos propios (`beforeAddRecordingEvent`, más abajo):
    // no hace falta filtrarlos además al crearlos, que costaría CPU en cada clic de la tablet.
    beforeSendTransaction: enmascararEvento,
    // Record a session replay when an error occurs (1 = 100% of error sessions).
    // Background sampling is kept low to stay within the free tier (50/month).
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.05,
    integrations: [
      // Solo con muestreo > 0: con 0 (local, tests, staging sin configurar) no se instrumenta nada.
      ...(tracesSampleRate > 0 ? [Sentry.browserTracingIntegration()] : []),
      Sentry.replayIntegration({
        // Mask all text and block all media by default — members' data stays private.
        maskAllText: true,
        blockAllMedia: true,
        // Los recursos de red y la navegación del replay también llevan URLs.
        beforeAddRecordingEvent: enmascararEventoDelReplay,
      }),
    ],
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Network Error",
    ],
    // Scripts que otros inyectan en la página y cuyos fallos no son nuestros.
    // Kaspersky mete `__ks_user_classic_regular.js` en cada web que visita el
    // usuario; el 14-09-2026 nos dejó 16 «Cannot create proxy…» con 0 usuarios.
    denyUrls: [
      /__ks_user_/i,
      /kaspersky/i,
    ],
    beforeSend(event, hint) {
      const originalException = hint?.originalException;
      const message =
        originalException?.message ||
        event?.exception?.values?.[0]?.value ||
        "";

      if (message.includes("No se pudo conectar al backend")) {
        return null;
      }

      return enmascararEvento(event);
    },
  };
};

export const initSentry = () => {
  if (!sentryEnabled) {
    return;
  }

  Sentry.init(construirOpcionesDeSentry());
};

export default Sentry;
